import { Chem, Pair } from "#/declarations";
import { GraphConfig } from "../../graph/graph-config";

export interface Sample {
  id?: string; // id is optional because sometimes it is the object key
  $key: string; // key = id
  name: string;
  cas: string;
  description: string;
  category: string;
  compound: string;
  availableAfter?: Array<string>;
  // ranges?: any;
  ir?: IrData;
  nmr?: NmrData;
  mass?: number;
  lockedFeatures?: Array<string>;
  progress?: number;
  free?: boolean;
  notes?: any;
}

/**
 * just the ranges, as used by sample service and graph views
 * @see {@link SampleService["sampleRanges"]}
 **/
export type SampleDataRanges = Required<Pick<Sample, "ir" | "nmr">>;

/**
 * Type predicate for {@link Sample}
 */
export function isSample(x: unknown): x is Sample {
  return (
    typeof x === "object" &&
    x !== null &&
    "$key" in x &&
    typeof x.$key === "string" &&
    "name" in x &&
    typeof x.name === "string" &&
    "cas" in x &&
    typeof x.cas === "string" &&
    "description" in x &&
    typeof x.description === "string" &&
    "compound" in x &&
    typeof x.compound === "string"
  );
}

export function isSampleDataRanges(x: unknown): x is SampleDataRanges {
  return (
    typeof x === "object" &&
    x !== null &&
    "ir" in x &&
    "nmr" in x &&
    isSpectroscopy(x.nmr, isRangeNmr) &&
    isSpectroscopy(x.ir, isRangeIr)
  );
}

export function isSpectroscopy<R extends Chem.Range>(
  x: unknown,
  rangePredicate?: (r: Chem.RangeBase) => r is R,
): x is Spectroscopy<R> {
  return (
    typeof x === "object" &&
    x !== null &&
    "ranges" in x &&
    Array.isArray(x.ranges) &&
    (!("graph" in x) || x.graph === undefined || isGraphConfig(x.graph)) &&
    x.ranges.every(rangePredicate ?? isRangeBase)
  );
}

export function isRange(x: unknown): x is Chem.Range {
  return (
    isRangeBase(x) &&
    isRangeType(x.type) &&
    (x.type === "nmr" ? isRangeNmr(x) : x.type === "ir" ? isRangeIr(x) : false)
  );
}

export function isRangeType(x: unknown): x is Chem.RangeType {
  return x === "nmr" || x === "ir";
}

export function isRangeBase(x: unknown): x is Chem.RangeBase {
  return (
    typeof x === "object" &&
    x !== null &&
    "$key" in x &&
    "type" in x &&
    "range" in x &&
    "labels" in x &&
    "assigned" in x &&
    typeof x.$key === "string" &&
    isRangeType(x.type) &&
    Array.isArray(x.range) &&
    Array.isArray(x.labels) &&
    (x.assigned === null || isGroup(x.assigned))
  );
}

export function isRangeIr(x: Chem.RangeBase): x is Chem.RangeIr {
  return (
    x.type === "ir" &&
    "shape" in x &&
    "intensity" in x &&
    typeof x.shape === "string" &&
    typeof x.intensity === "string" &&
    (!("nujol" in x) || x.nujol === undefined || typeof x === "boolean")
  );
}
export function isRangeNmr(x: Chem.RangeBase): x is Chem.RangeNmr {
  return (
    x.type === "nmr" &&
    "integration" in x &&
    "splitting" in x &&
    "neighbours" in x &&
    Array.isArray(x.integration) &&
    x.integration.every((n) => typeof n === "number") &&
    typeof x.splitting === "string" &&
    typeof x.neighbours === "number"
  );
}

/** STUB */
export function isGroup(x: unknown) {
  return typeof x === "object" && x !== null;
}

/** STUB */
export function isGraphConfig(x: unknown) {
  return typeof x === "object" && x !== null;
}

export interface Spectroscopy<RangeType extends Chem.Range> {
  ranges: RangeType[];
  graph?: GraphConfig;
}

export interface IrData extends Spectroscopy<Chem.RangeIr> {
  fingerprint?: Pair<number>;
}

export interface NmrData extends Spectroscopy<Chem.RangeNmr> {}
